import { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useClaudeReview } from './useClaudeReview';
import { Finding } from './types';

const useStyles = makeStyles(theme => ({
  root: { height: '100%' },
  metricBox: {
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  metricNumber: { fontWeight: 700, fontSize: '1.8rem' },
  findingRow: { marginBottom: theme.spacing(1) },
  chip: { marginRight: theme.spacing(0.5) },
  error: { color: theme.palette.error.main },
  lastRun: { color: theme.palette.text.secondary, fontSize: '0.75rem' },
}));

const SEVERITY_COLORS: Record<Finding['severity'], 'default' | 'primary' | 'secondary'> = {
  high: 'secondary',
  medium: 'default',
  low: 'primary',
};

export function EntityClaudeReviewCard() {
  const classes = useStyles();
  const { entity } = useEntity();

  const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const repoSlug =
    (entity.metadata.annotations?.['github.com/project-slug'] || entity.metadata.annotations?.['backstage.io/view-url']) ?? '';

  const { run, loading, error, jobStatus, isFullScan, fetchLatest, runAnalysis } = useClaudeReview(
    entityRef,
    repoSlug,
  );

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  return (
    <Card className={classes.root}>
      <CardHeader
        title="Claude Code Review"
        subheader={
          run ? (
            <span className={classes.lastRun}>
              Last run: {new Date(run.ran_at).toLocaleString()}
            </span>
          ) : (
            <span className={classes.lastRun}>No analysis run yet</span>
          )
        }
      />
      <Divider />
      <CardContent>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            <CircularProgress />
            {jobStatus && jobStatus !== 'done' && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                {jobStatus === 'pending' && 'Queued — waiting to start…'}
                {jobStatus === 'running' && (isFullScan
                  ? 'Full scan in progress… this may take a few minutes'
                  : 'Analyzing changed files…')}
              </Typography>
            )}
          </Box>
        )}

        {!loading && error && (
          <Typography className={classes.error}>{error}</Typography>
        )}

        {!loading && !error && run && (
          <>
            <Grid container spacing={2}>
              {(
                [
                  { label: 'High', value: run.summary.high, color: '#f44336' },
                  { label: 'Medium', value: run.summary.medium, color: '#ff9800' },
                  { label: 'Low', value: run.summary.low, color: '#4caf50' },
                ] as const
              ).map(({ label, value, color }) => (
                <Grid item xs={4} key={label}>
                  <Box className={classes.metricBox}>
                    <Typography className={classes.metricNumber} style={{ color }}>
                      {value}
                    </Typography>
                    <Typography variant="caption">{label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {run.findings.length > 0 && (
              <>
                <Box mt={2} mb={1}>
                  <Divider />
                </Box>
                {(expanded ? run.findings : run.findings.slice(0, 5)).map((finding, i) => (
                  <Box key={i} className={classes.findingRow}>
                    <Typography variant="body2">
                      <strong>{finding.file}</strong>
                      {finding.line !== null && `:${finding.line}`}
                    </Typography>
                    <Box mb={0.5}>
                      <Chip
                        size="small"
                        label={finding.severity.toUpperCase()}
                        color={SEVERITY_COLORS[finding.severity]}
                        className={classes.chip}
                      />
                      <Chip
                        size="small"
                        label={finding.category.replace('_', ' ')}
                        className={classes.chip}
                      />
                    </Box>
                    <Typography variant="body2">{finding.issue}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      → {finding.suggestion}
                    </Typography>
                  </Box>
                ))}
                {run.findings.length > 5 && (
                  <Button size="small" onClick={() => setExpanded(e => !e)}>
                    {expanded
                      ? 'Show less'
                      : `+${run.findings.length - 5} more findings`}
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {!loading && !error && !run && (
          <Typography variant="body2" color="textSecondary">
            Run an analysis to see code quality findings for this repository.
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={() => runAnalysis(false)}
          disabled={loading}
        >
          Run Analysis
        </Button>
        <Button
          size="small"
          color="default"
          variant="outlined"
          onClick={() => runAnalysis(true)}
          disabled={loading}
        >
          Full Scan
        </Button>
      </CardActions>
    </Card>
  );
}
